<template>
  <v-container class="my-0 ml-0 mr-n2 pa-0" fluid>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-2">
        <!-- Reserved for intro/description if needed -->
      </v-col>
    </v-row>

    <!-- LLMs Header -->
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-2">
        <h3>LLMs</h3>
      </v-col>
    </v-row>

    <!-- LLMs Controls -->
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" density="compact" class="ma-0 pa-2 d-flex align-center">
        <Button
          buttonClass="ml-1"
          icon="mdi-plus"
          v-if="count > 0"
          :click="openCreateModelOverlay"
          size="x-large"
          tooltip="New Model"
          variant="outlined"
          density="default"
          color="secondary"
        />
        <v-btn-toggle v-model="sortOrder" mandatory class="ml-4" density="compact">
          <v-btn icon="mdi-sort-variant-remove" value="default" size="small" title="Default Order"></v-btn>
          <v-btn icon="mdi-sort-alphabetical-ascending" value="asc" size="small" title="Sort Ascending"></v-btn>
          <v-btn icon="mdi-sort-alphabetical-descending" value="desc" size="small" title="Sort Descending"></v-btn>
        </v-btn-toggle>
        <v-spacer></v-spacer>
        <v-tooltip text="Refresh Models" location="top">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" color="primary" variant="text" icon :loading="refreshingModels" @click="refreshModelsView(true, 'manual')">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>

    <!-- LLMs Grid -->
    <v-row density="compact" class="ma-0 pa-0" v-if="modelIds != null">
      <v-col v-for="item in sortedModelIds" :key="item.hash" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <ModelSummaryCard
          v-if="isDesktop"
          :model="modelConfigById(item.id)"
          @edit="openEditModelOverlay"
        />
        <ModelCard
          v-else
          :modelId="item.id"
          :showTips="showTips"
          :startExpanded="modelNameById(item.id) === expandedModelName"
          @saved="handleModelSaved"
        />
      </v-col>
      <v-col v-if="count === 0" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <v-card
          @click="openCreateModelOverlay"
          class="ma-0 pa-2 card add-card"
          color="surface"
          density="compact"
          variant="elevated"
          elevation="4"
          min-height="350"
        >
          <p>New model</p>
          <v-icon class="add-icon" icon="mdi-plus"></v-icon>
        </v-card>
      </v-col>
    </v-row>

    <!-- Voices Header -->
    <v-row density="compact" class="ma-0 pa-0 mt-6">
      <v-col cols="12" class="ma-0 pa-2 d-flex align-center">
        <h3 class="mr-4">Voices</h3>
        <Button
          buttonClass="ml-1"
          icon="mdi-plus"
          v-if="voicesCount > 0"
          :click="openCreateVoiceOverlay"
          size="x-large"
          tooltip="New Voice"
          variant="outlined"
          density="default"
          color="secondary"
        />
        <v-btn-toggle v-model="voiceSortOrder" mandatory class="ml-4" density="compact">
          <v-btn icon="mdi-sort-variant-remove" value="default" size="small" title="Default Order"></v-btn>
          <v-btn icon="mdi-sort-alphabetical-ascending" value="asc" size="small" title="Sort Ascending"></v-btn>
          <v-btn icon="mdi-sort-alphabetical-descending" value="desc" size="small" title="Sort Descending"></v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <!-- Voices Grid -->
    <v-row density="compact" class="ma-0 pa-0">
      <v-col v-for="item in sortedVoiceIds" :key="item.hash" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <VoiceSummaryCard
          v-if="isDesktop"
          :voice="voiceConfigById(item.id)"
          @edit="openEditVoiceOverlay"
        />
        <VoiceCard
          v-else
          :voiceId="item.id"
          :startExpanded="voiceNameById(item.id) === expandedVoiceName"
          @saved="handleVoiceSaved"
        />
      </v-col>
      <v-col v-if="voicesCount === 0" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <v-card
          @click="openCreateVoiceOverlay"
          class="ma-0 pa-2 card add-card"
          color="surface"
          density="compact"
          variant="elevated"
          elevation="12"
          min-height="350"
        >
          <p>New voice</p>
          <v-icon class="add-icon" icon="mdi-plus"></v-icon>
        </v-card>
      </v-col>
    </v-row>
    <ConfigCardOverlay
      v-model="showCreateModelOverlay"
      title="New Model"
      @close="closeCreateModelOverlay"
    >
      <ModelCard
        isOverlay
        :showTips="showTips"
        @remove-card="closeCreateModelOverlay"
        @saved="handleModelSaved"
      />
    </ConfigCardOverlay>
    <ConfigCardOverlay
      v-model="showEditModelOverlay"
      title="Edit Model"
      @close="closeEditModelOverlay"
    >
      <ModelCard
        v-if="editingModelId"
        :modelId="editingModelId"
        isOverlay
        :showTips="showTips"
        :startExpanded="true"
        @remove-card="closeEditModelOverlay"
        @saved="handleModelSaved"
      />
    </ConfigCardOverlay>
    <ConfigCardOverlay
      v-model="showCreateVoiceOverlay"
      title="New Voice"
      @close="closeCreateVoiceOverlay"
    >
      <VoiceCard
        isOverlay
        @remove-card="closeCreateVoiceOverlay"
        @saved="handleVoiceSaved"
      />
    </ConfigCardOverlay>
    <ConfigCardOverlay
      v-model="showEditVoiceOverlay"
      title="Edit Voice"
      @close="closeEditVoiceOverlay"
    >
      <VoiceCard
        v-if="editingVoiceId"
        :voiceId="editingVoiceId"
        isOverlay
        :startExpanded="true"
        @saved="handleVoiceSaved"
      />
    </ConfigCardOverlay>
  </v-container>
</template>
<script lang="ts">
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import ModelCard from '@/components/cards/ModelCard.vue'
import ModelSummaryCard from '@/components/cards/ModelSummaryCard.vue'
import VoiceCard from '@/components/cards/VoiceCard.vue'
import VoiceSummaryCard from '@/components/cards/VoiceSummaryCard.vue'
import ConfigCardOverlay from '@/components/overlays/ConfigCardOverlay.vue'
import Button from '@/components/sub/Button.vue'
import type { ConfigRefreshTarget } from '@/store/global'
import { ModelConfig, UserModelConfig } from '@/types/models'
import { createHash } from '@/utils/general'
import { Options, mixins } from 'vue-class-component'
import { mapGetters } from 'vuex'

@Options({
  components: {
    ModelCard,
    ModelSummaryCard,
    VoiceCard,
    VoiceSummaryCard,
    ConfigCardOverlay,
    Button
  },
  computed: {
    ...mapGetters([
      // models
      'modelConfigs', 'modelConfigsById', 'modelConfigById',
      // voices
      'voiceConfigs', 'voiceConfigsById', 'voiceConfigById',
      // global
      'selectedTab', 'configRefreshToken'
    ])
  },
  watch: {
    modelConfigs: {
      handler() {
        this.updateModelIdsWithHash()
      },
      deep: true
    },
    voiceConfigs: {
      handler() {
        this.updateVoiceIdsWithHash()
      },
      deep: true
    },
    selectedTab(value: string) {
      if (value === 'models') {
        void this.maybeRefreshModelsView()
      }
    },
    modelsRefreshToken() {
      void this.maybeRefreshModelsView()
    }
  }
})
export default class Models extends mixins(AnalyticsMixin) {
  public showTips = false

  // Models store bindings
  public modelConfigs!: Record<string, ModelConfig>
  public modelConfigsById!: Record<string, ModelConfig>
  public modelConfigById!: (id: string) => UserModelConfig

  // Voices store bindings
  public voiceConfigs!: Record<string, any>
  public voiceConfigsById!: Record<string, any>
  public voiceConfigById!: (id: string) => any
  public selectedTab!: string
  public configRefreshToken!: (target: ConfigRefreshTarget) => number

  public showCreateModelOverlay = false
  public showEditModelOverlay = false
  public editingModelId: string | null = null
  public showCreateVoiceOverlay = false
  public showEditVoiceOverlay = false
  public editingVoiceId: string | null = null
  public expandedModelName: string | null = null
  public expandedVoiceName: string | null = null
  public refreshingModels = false
  public lastHandledModelsRefreshToken = -1

  private _modelIdsWithHash: { id: string; hash: string }[] = []
  private sortOrder: 'default' | 'asc' | 'desc' = 'default'

  private _voiceIdsWithHash: { id: string; hash: string }[] = []
  private voiceSortOrder: 'default' | 'asc' | 'desc' = 'default'

  // Computed properties (Models)
  get modelIds(): { id: string; hash: string }[] {
    return this._modelIdsWithHash
  }

  get sortedModelIds(): { id: string; hash: string }[] {
    if (this.sortOrder === 'default') {
      return this._modelIdsWithHash
    }

    const sorted = [...this._modelIdsWithHash].sort((a, b) => {
      const modelA = this.modelConfigById(a.id)
      const modelB = this.modelConfigById(b.id)

      if (!modelA || !modelB) return 0

      const nameA = modelA.config.name.toLowerCase()
      const nameB = modelB.config.name.toLowerCase()

      if (this.sortOrder === 'asc') {
        return nameA.localeCompare(nameB)
      } else {
        return nameB.localeCompare(nameA)
      }
    })

    return sorted
  }

  get count(): number {
    return this.sortedModelIds.length
  }

  get modelsRefreshToken(): number {
    return this.configRefreshToken('models')
  }

  // Computed properties (Voices)
  get voiceIds(): { id: string; hash: string }[] {
    return this._voiceIdsWithHash
  }

  get sortedVoiceIds(): { id: string; hash: string }[] {
    if (this.voiceSortOrder === 'default') return this._voiceIdsWithHash
    const sorted = [...this._voiceIdsWithHash].sort((a, b) => {
      const va = this.voiceConfigById(a.id)
      const vb = this.voiceConfigById(b.id)
      if (!va || !vb) return 0
      const na = (va.name || '').toLowerCase()
      const nb = (vb.name || '').toLowerCase()
      return this.voiceSortOrder === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na)
    })
    return sorted
  }

  get voicesCount(): number {
    return this.sortedVoiceIds.length
  }

  get isDesktop(): boolean {
    return (this as any).$vuetify.display.mdAndUp
  }

  async mounted() {
    if (this.selectedTab === 'models') {
      await this.refreshModelsView(true, 'initial')
    }

    this.trackComponentView('models_tab', {
      model_count: this.count,
      voice_count: this.voicesCount
    })
  }

  // Method to update model IDs with hash
  async updateModelIdsWithHash() {
    if (!this.modelConfigsById) return

    const modelIdsOnly = Object.keys(this.modelConfigsById || {})
    const modelIds: { id: string; hash: string }[] = []

    for (const id of modelIdsOnly) {
      const config = this.modelConfigsById[id]
      if (config) {
        modelIds.push({ id, hash: await createHash(config) })
      }
    }

    this._modelIdsWithHash = modelIds
  }

  // Method to update voice IDs with hash
  async updateVoiceIdsWithHash() {
    if (!this.voiceConfigsById) return
    const ids = Object.keys(this.voiceConfigsById || {})
    const result: { id: string; hash: string }[] = []
    for (const id of ids) {
      const cfg = this.voiceConfigsById[id]
      if (cfg) {
        result.push({ id, hash: await createHash(cfg) })
      }
    }
    this._voiceIdsWithHash = result
  }

  modelNameById(id: string): string | null {
    const model = this.modelConfigById(id)
    return model?.config?.name ?? null
  }

  voiceNameById(id: string): string | null {
    const voice = this.voiceConfigById(id)
    return voice?.name ?? null
  }

  openCreateModelOverlay() {
    this.trackButtonClick('create_model_start', 'click', {
      model_count: this.count
    })
    this.showCreateModelOverlay = true
  }

  closeCreateModelOverlay() {
    this.showCreateModelOverlay = false
  }

  openEditModelOverlay(modelId: string) {
    this.editingModelId = modelId
    this.showEditModelOverlay = true
  }

  closeEditModelOverlay() {
    this.showEditModelOverlay = false
    this.editingModelId = null
  }

  async refreshModelsView(force = false, source: 'initial' | 'manual' | 'sync' = 'sync') {
    if (this.refreshingModels) return

    if (source === 'manual') {
      this.trackButtonClick('refresh_models', 'click', {
        model_count: this.count,
        voice_count: this.voicesCount
      })
    }

    this.refreshingModels = true
    try {
      await Promise.all([
        this.$store.dispatch('fetchProviders'),
        this.$store.dispatch('fetchModels'),
        this.$store.dispatch('fetchModelConfigs'),
        this.$store.dispatch('fetchVoices')
      ])
      await this.prefetchModelPricing()
      await this.updateModelIdsWithHash()
      await this.updateVoiceIdsWithHash()
      this.lastHandledModelsRefreshToken = this.modelsRefreshToken
    } finally {
      this.refreshingModels = false
    }
  }

  async maybeRefreshModelsView(force = false) {
    if (this.selectedTab !== 'models' && !force) return
    const needsRefresh = force || this.lastHandledModelsRefreshToken !== this.modelsRefreshToken
    if (needsRefresh) {
      await this.refreshModelsView(force, force ? 'manual' : 'sync')
    }
  }

  handleModelSaved(payload?: { name?: string }) {
    if (payload?.name) {
      this.expandedModelName = payload.name
    }
    void this.prefetchModelPricing()
    this.lastHandledModelsRefreshToken = this.modelsRefreshToken
    this.showCreateModelOverlay = false
    this.showEditModelOverlay = false
  }

  openCreateVoiceOverlay() {
    this.trackButtonClick('create_voice_start', 'click', {
      voice_count: this.voicesCount
    })
    this.showCreateVoiceOverlay = true
  }

  closeCreateVoiceOverlay() {
    this.showCreateVoiceOverlay = false
  }

  openEditVoiceOverlay(voiceId: string) {
    this.editingVoiceId = voiceId
    this.showEditVoiceOverlay = true
  }

  closeEditVoiceOverlay() {
    this.showEditVoiceOverlay = false
    this.editingVoiceId = null
  }

  handleVoiceSaved(payload?: { name?: string }) {
    if (payload?.name) {
      this.expandedVoiceName = payload.name
    }
    this.showCreateVoiceOverlay = false
    this.showEditVoiceOverlay = false
  }

  async prefetchModelPricing() {
    const items = Object.values(this.modelConfigsById || {})
      .map((config) => ({
        providerKey: config.providerKey,
        model: config.model
      }))
      .filter((item) => !!item.providerKey && !!item.model)

    if (items.length === 0) return
    await this.$store.dispatch('fetchModelPricing', { items })
  }
}
</script>
<style scoped lang="scss">
.add-card {
  border: 1px solid rgba(255, 255, 255, 0.3);
  height: 350px;
  text-align: center;
  cursor: pointer;

  p {
    margin-top: 130px;
  }
}

.add-card-small {
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-align: center;
  cursor: pointer;
}
</style>
